/**
 * PostmortemAnalysis Component
 * 
 * Main dashboard for startup intelligence postmortem analysis. Provides detailed
 * insights into why startups failed, their characteristics, and failure patterns.
 * Combines survival statistics with analysis by reasons for failure,  locations,
 * industries, lifespans, funding rounds, and more.
 * 
 * Features:
 * - Company failure statistics and reasons analysis
 * - Multiple breakdown filters (industry, location, gender, employees, etc.)
 * - Dashboard statistics with data quality footnotes
 * - Collapsible analysis sections
 * - Loading states and error handling
 * 
 * @returns {JSX.Element} Postmortem analysis dashboard with multiple analytics filters\n */
import React, { useState, useEffect } from "react";
import Survival from "./filters/survival.js";
import Reasons from "./filters/reasons.js";
import Locations from "./filters/locations.js";
import Industries from "./filters/industries.js";
import Lifespans from "./filters/lifespans.js";
import FundingRounds from "./filters/funding-rounds.js";
import FundingAmounts from "./filters/funding-amounts.js";
import Employees from "./filters/employees.js";
import GenderStatistics from "./filters/gender-statistics.js";
import NavBar from "../../layout/navbar/navbar";
import "./postmortem-analysis.css";
import { Accordion, Container, Alert, Spinner, Button } from "react-bootstrap";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { BiAnalyse } from "react-icons/bi";
import { IoMdInformationCircleOutline, IoMdClose } from "react-icons/io";
import { FaChevronLeft, FaChevronDown, FaEllipsisH } from "react-icons/fa";
import { useSupabaseApp } from "../../SupabaseApp.js";

const PostmortemAnalysis = () => {
  // State to control alert visibility
  const [alert, setAlert] = useState(true);

  // State to store alert message content with loading indicator
  const [message, setMessage] = useState(
    <div style={{ display: "flex", paddingTop: "10px" }}>
      <div style={{ width: "90%" }}>
        <p style={{ color: "#0f9d58" }}>
          <BiAnalyse className="alert-icon" /> Loading
        </p>
        <p style={{ marginLeft: "28px", fontSize: "14px" }}>
          Won't be long now. We're always doing our best to reduce waiting time.
        </p>
        <p style={{ marginLeft: "28px", fontSize: "14px" }}>
          If your waiting time is too long,{" "}
          <a
            href="https://www.roiquant.com/contact"
            target="_blank"
            rel="noopener noreferrer"
          >
            contact us
          </a>
          .
        </p>
      </div>
      <div style={{ width: "10%", textAlign: "right" }}>
        <Spinner animation="border" variant="secondary" />
      </div>
    </div>,
  );

  // Close alert and clear message
  function closeAlert() {
    setAlert(false);
    setMessage("");
  }

  // Get current authenticated user and Supabase client from context
  const { currentUser, supabase } = useSupabaseApp();

  // Redirect to login if not authenticated
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // State to store main dashboard statistics (inactive companies count and failure reasons)
  const [dashboardStatistics, setDashboardStatistics] = useState({
    postmortemAnalysisInactiveCompanies: 0,
    postmortemAnalysisReasonsForFailure: 0,
  });

  // State to store detailed footnotes statistics for data quality/completeness
  const [footnotesStats, setFootnotesStats] = useState({
    uniqueReasons: 0,
    companiesWithReasons: 0,
    companiesWithoutReasons: 0,
    companiesWithLocation: 0,
    companiesWithoutLocation: 0,
    uniqueIndustries: 0,
    companiesWithIndustries: 0,
    companiesWithoutIndustries: 0,
    companiesWithLifespans: 0,
    companiesWithoutLifespans: 0,
    companiesWithFundingRounds: 0,
    companiesWithoutFundingRounds: 0,
    companiesWithFundingAmounts: 0,
    companiesWithoutFundingAmounts: 0,
    companiesWithEmployees: 0,
    companiesWithoutEmployees: 0,
    companiesWithGenderData: 0,
    companiesWithoutGenderData: 0,
  });

  // Fetch main dashboard statistics from Supabase on component mount
  useEffect(() => {
    async function fetchDashboardStatistics() {
      try {
        let { data, error } = await supabase.functions.invoke(
          "getDashboardStatistics",
        );
        if (error) {
          const ctx = await error.context?.json?.().catch(() => ({}));
          const msg = ctx?.error ?? "unknown";
          throw new Error(msg);
        }
        console.log("dashboard stats data:", data);
        setDashboardStatistics(data);
        // console.log("current stats:", dashboardStatistics);

        closeAlert();
      } catch (err) {
        setMessage(
          <div style={{ paddingTop: "10px" }}>
            <p style={{ fontSize: "16px", color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Bad internet
              issues or server connection errors
            </p>
            <p style={{ marginLeft: "28px", fontSize: "14px" }}>
              Sorry, there seems to be poor internet connection or interrupted
              connectivity issues or server connection errors. Should the issues
              persist,{" "}
              <a
                href="https://www.roiquant.com/contact"
                target="_blank"
                rel="noopener noreferrer"
              >
                contact us
              </a>
              .
            </p>
          </div>,
        );
      }
    }
    fetchDashboardStatistics();
  }, [supabase]);

  // Fetch footnotes/data quality statistics from Supabase on component mount
  useEffect(() => {
    async function fetchFootnotesStats() {
      try {
        let { data, error } = await supabase.functions.invoke(
          "getPostmortemAnalysisStatistics",
        );

        if (error) {
          const ctx = await error.context?.json?.().catch(() => ({}));
          const msg = ctx?.error ?? "unknown";
          throw new Error(msg);
        }
        setFootnotesStats(data);
        // console.log("current stats:", dashboardStatistics);
      } catch (err) { }
    }
    fetchFootnotesStats();
  }, [supabase]);

  // State to track expansion/collapse status of each analysis section (arrow icon states)
  const [changeArrow, setChangeArrow] = useState({
    survival: false,
    reasons: false,
    locations: false,
    industries: false,
    lifespans: false,
    fundingRounds: false,
    fundingAmounts: false,
    employees: false,
    genderStatistics: false,
  });

  // Toggle functions to expand/collapse analysis sections

  // Toggle survival section expansion
  function changeSurvival() {
    setChangeArrow({ survival: !changeArrow.survival });
  }

  // Toggle reasons section expansion
  function changeReasons() {
    setChangeArrow({ reasons: !changeArrow.reasons });
  }

  // Toggle locations section expansion
  function changeLocations() {
    setChangeArrow({ locations: !changeArrow.locations });
  }

  // Toggle industries section expansion
  function changeIndustries() {
    setChangeArrow({ industries: !changeArrow.industries });
  }

  // Toggle lifespans section expansion
  function changeLifespans() {
    setChangeArrow({ lifespans: !changeArrow.lifespans });
  }

  // Toggle funding rounds section expansion
  function changeFundingRounds() {
    setChangeArrow({ fundingRounds: !changeArrow.fundingRounds });
  }

  // Toggle funding amounts section expansion
  function changeFundingAmounts() {
    setChangeArrow({ fundingAmounts: !changeArrow.fundingAmounts });
  }

  // Toggle employees section expansion
  function changeEmployees() {
    setChangeArrow({ employees: !changeArrow.employees });
  }

  // Toggle gender statistics section expansion
  function changeGenderStatistics() {
    setChangeArrow({ genderStatistics: !changeArrow.genderStatistics });
  }

  return (
    <>
      {/* loading alert */}

      {alert ? (
        <Alert className="error-message floating-alert" variant="light">
          <div>
            <div style={{ textAlign: "right" }}>
              <Button className="pitchdeckmessageclose" onClick={closeAlert}>
                <IoMdClose />
              </Button>
            </div>
            {message}
          </div>
        </Alert>
      ) : (
        ""
      )}

      {/* navbar */}
      <NavBar />

      {/* postmortem analysis */}
      <div id="postmortemanalysis">
        <Container className="col-11" style={{ paddingBottom: "200px" }}>
          <div className="postmortemanalysisheaderdiv">
            {/* title and info note */}
            <div className="postmortemanalysistitlediv">
              <br />
              <span className="postmortemanalysistext">
                Postmortem analysis
                <sup data-tip="Examine the pitfalls and mistakes others had made with our structured frameworks.">
                  <IoMdInformationCircleOutline id="information-icon" />
                </sup>
              </span>
            </div>

            {/* statistics table */}
            <div className="postmortemanalysisstatsdiv">
              <table className="tg" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <td className="table-data db-header">Inactive companies</td>
                    <td className="table-data db-header">
                      Reasons for shutdown
                    </td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="table-data db-data">
                      {(
                        dashboardStatistics.postmortemAnalysisInactiveCompanies ??
                        0
                      )
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    </td>
                    <td className="table-data db-data">
                      {(
                        dashboardStatistics.postmortemAnalysisReasonsForFailure ??
                        0
                      )
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* accordions as topics */}
          <div className="tab-body">
            {/* reasons for shutdown */}
            <Accordion>
              <Accordion.Item eventKey="reasons">
                <Accordion.Header onClick={changeReasons}>
                  <div className="companyarrowicon">
                    {changeArrow.reasons ? (
                      <FaChevronDown />
                    ) : (
                      <FaChevronLeft />
                    )}
                  </div>
                  <div className="companytitlecard">
                    What are the top reasons for shutdown?
                  </div>
                  <div className="companyellipsisicon">
                    <FaEllipsisH />
                  </div>
                </Accordion.Header>

                <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                  <Reasons
                    uniqueReasons={footnotesStats.uniqueReasons}
                    companiesWithReasons={footnotesStats.companiesWithReasons}
                    companiesWithoutReasons={
                      footnotesStats.companiesWithoutReasons
                    }
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {/* locations */}
            <Accordion style={{ marginTop: "10px" }}>
              <Accordion.Item eventKey="locations">
                <Accordion.Header onClick={changeLocations}>
                  <div className="companyarrowicon">
                    {changeArrow.locations ? (
                      <FaChevronDown />
                    ) : (
                      <FaChevronLeft />
                    )}
                  </div>

                  <div className="companytitlecard">
                    Where did the companies base at?
                  </div>

                  <div className="companyellipsisicon">
                    <FaEllipsisH />
                  </div>
                </Accordion.Header>

                <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                  <Locations
                    companiesWithLocation={footnotesStats.companiesWithLocation}
                    companiesWithoutLocation={
                      footnotesStats.companiesWithoutLocation
                    }
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {/* industries */}
            <Accordion style={{ marginTop: "10px" }}>
              <Accordion.Item eventKey="industries">
                <Accordion.Header onClick={changeIndustries}>
                  <div className="companyarrowicon">
                    {changeArrow.industries ? (
                      <FaChevronDown />
                    ) : (
                      <FaChevronLeft />
                    )}
                  </div>

                  <div className="companytitlecard">
                    Which industries did the companies operate in?
                  </div>

                  <div className="companyellipsisicon">
                    <FaEllipsisH />
                  </div>
                </Accordion.Header>

                <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                  <Industries
                    uniqueIndustries={footnotesStats.uniqueIndustries}
                    companiesWithIndustries={
                      footnotesStats.companiesWithIndustries
                    }
                    companiesWithoutIndustries={
                      footnotesStats.companiesWithoutIndustries
                    }
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {/* lifespans */}
            <Accordion style={{ marginTop: "10px" }}>
              <Accordion.Item eventKey="lifespans">
                <Accordion.Header onClick={changeLifespans}>
                  <div className="companyarrowicon">
                    {changeArrow.lifespans ? (
                      <FaChevronDown />
                    ) : (
                      <FaChevronLeft />
                    )}
                  </div>

                  <div className="companytitlecard">
                    What were the lifespans of the companies?
                  </div>

                  <div className="companyellipsisicon">
                    <FaEllipsisH />
                  </div>
                </Accordion.Header>

                <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                  <Lifespans
                    companiesWithLifespans={
                      footnotesStats.companiesWithLifespans
                    }
                    companiesWithoutLifespans={
                      footnotesStats.companiesWithoutLifespans
                    }
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {/* funding rounds */}
            <Accordion style={{ marginTop: "10px" }}>
              <Accordion.Item eventKey="fundingRounds">
                <Accordion.Header onClick={changeFundingRounds}>
                  <div className="companyarrowicon">
                    {changeArrow.fundingRounds ? (
                      <FaChevronDown />
                    ) : (
                      <FaChevronLeft />
                    )}
                  </div>

                  <div className="companytitlecard">
                    What were the last funding rounds of the companies?
                  </div>

                  <div className="companyellipsisicon">
                    <FaEllipsisH />
                  </div>
                </Accordion.Header>

                <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                  <FundingRounds
                    companiesWithFundingRounds={
                      footnotesStats.companiesWithFundingRounds
                    }
                    companiesWithoutFundingRounds={
                      footnotesStats.companiesWithoutFundingRounds
                    }
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {/* funding amounts */}
            <Accordion style={{ marginTop: "10px" }}>
              <Accordion.Item eventKey="fundingAmounts">
                <Accordion.Header onClick={changeFundingAmounts}>
                  <div className="companyarrowicon">
                    {changeArrow.fundingAmounts ? (
                      <FaChevronDown />
                    ) : (
                      <FaChevronLeft />
                    )}
                  </div>

                  <div className="companytitlecard">
                    What were the total funding amounts raised by the companies?
                  </div>

                  <div className="companyellipsisicon">
                    <FaEllipsisH />
                  </div>
                </Accordion.Header>

                <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                  <FundingAmounts
                    companiesWithFundingAmounts={
                      footnotesStats.companiesWithFundingAmounts
                    }
                    companiesWithoutFundingAmounts={
                      footnotesStats.companiesWithoutFundingAmounts
                    }
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {/* employees */}
            <Accordion style={{ marginTop: "10px" }}>
              <Accordion.Item eventKey="employees">
                <Accordion.Header onClick={changeEmployees}>
                  <div className="companyarrowicon">
                    {changeArrow.employees ? (
                      <FaChevronDown />
                    ) : (
                      <FaChevronLeft />
                    )}
                  </div>

                  <div className="companytitlecard">
                    How many employees did the companies hire?
                  </div>

                  <div className="companyellipsisicon">
                    <FaEllipsisH />
                  </div>
                </Accordion.Header>

                <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                  <Employees
                    companiesWithEmployees={
                      footnotesStats.companiesWithEmployees
                    }
                    companiesWithoutEmployees={
                      footnotesStats.companiesWithoutEmployees
                    }
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {/* gender */}
            <Accordion style={{ marginTop: "10px" }}>
              <Accordion.Item eventKey="genderStatistics">
                <Accordion.Header onClick={changeGenderStatistics}>
                  <div className="companyarrowicon">
                    {changeArrow.genderStatistics ? (
                      <FaChevronDown />
                    ) : (
                      <FaChevronLeft />
                    )}
                  </div>

                  <div className="companytitlecard">
                    What is the gender statistics among the companies' founders?
                  </div>

                  <div className="companyellipsisicon">
                    <FaEllipsisH />
                  </div>
                </Accordion.Header>

                <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                  <GenderStatistics
                    companiesWithGenderData={
                      footnotesStats.companiesWithGenderData
                    }
                    companiesWithoutGenderData={
                      footnotesStats.companiesWithoutGenderData
                    }
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {/* survival chart */}
            <Accordion style={{ marginTop: "10px" }}>
              <Accordion.Item eventKey="survival">
                <Accordion.Header onClick={changeSurvival}>
                  <div className="companyarrowicon">
                    {changeArrow.survival ? (
                      <FaChevronDown />
                    ) : (
                      <FaChevronLeft />
                    )}
                  </div>

                  <div className="companytitlecard">
                    Bonus: survival rate of private sector establishments in the
                    United States by opening year
                  </div>

                  <div className="companyellipsisicon">
                    <FaEllipsisH />
                  </div>
                </Accordion.Header>

                <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                  <Survival />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </Container>
      </div>
    </>
  );
};

export default PostmortemAnalysis;
